// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { styled } from '@mui/material/styles';
import * as React from 'react';
import { Chevron } from '../../../lib/animated';
import { Card, Collapse, IconButton, Typography } from '../../../lib/base';
import { PageError } from '../error';
import { PageLoading } from '../loading';
import { StyledCardFirst, StyledCardHeader } from './TitleCard';

const ChevronButton = styled(IconButton)(({ theme }) => ({
  width: theme.spacing(5),
  height: theme.spacing(5),
  marginBottom: -theme.spacing(1),
  marginRight: -theme.spacing(1),
  marginTop: -theme.spacing(1),
}));

interface Props {
  readonly title: string;
  readonly content: React.ReactElement;
  readonly loading?: boolean;
  readonly loadingError?: boolean;
  readonly initialShowContent?: boolean;
  readonly rightElement?: React.ReactElement;
  readonly extraHeaderContent?: React.ReactElement;
  readonly onShowContent?: () => void;
}
export default function ExpandoCard({
  title,
  content,
  initialShowContent,
  loading,
  loadingError,
  extraHeaderContent,
  onShowContent: onShowContentProp,
}: Props): React.ReactElement {
  const [showContent, setShowContent] = React.useState(initialShowContent ?? false);
  const onShowContent = React.useCallback(() => {
    if (onShowContentProp) {
      onShowContentProp();
    }
    setShowContent(true);
  }, [onShowContentProp]);
  const onHideContent = React.useCallback(() => setShowContent(false), []);
  const onClickChevron = showContent ? onHideContent : onShowContent;

  return (
    <StyledCardFirst>
      <Card sx={{ display: 'flex', flexDirection: 'column' }}>
        <StyledCardHeader pointer={true} onClick={onClickChevron}>
          <Typography variant="h6">{title}</Typography>
          <ChevronButton onClick={onClickChevron}>
            <Chevron up={!showContent} />
          </ChevronButton>
        </StyledCardHeader>
        <Collapse in={showContent} timeout="auto">
          {extraHeaderContent && <StyledCardHeader>{extraHeaderContent}</StyledCardHeader>}
          {loading ? <PageLoading /> : loadingError ? <PageError /> : content}
        </Collapse>
      </Card>
    </StyledCardFirst>
  );
}
